// Iter 83.4 / Phase E4 Sprint 4 — GL Mappings Admin.
// System-seed view + operator overrides (D25). Provider tabs:
// Tally / Zoho. Override mutations bump gl_mappings_version (D29).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    PencilSimple, TrashSimple, MagnifyingGlass, ArrowsClockwise, FloppyDisk,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const NATURE_TONE = {
    earning: "bg-emerald-100 text-emerald-800",
    reimbursement: "bg-teal-100 text-teal-800",
    deduction: "bg-rose-100 text-rose-800",
    employer_contribution: "bg-amber-100 text-amber-800",
    bank: "bg-sky-100 text-sky-800",
    liability: "bg-violet-100 text-violet-800",
    asset: "bg-blue-100 text-blue-800",
    asset_outflow: "bg-blue-100 text-blue-800",
    expense: "bg-orange-100 text-orange-800",
};

export default function GlMappingsAdmin() {
    const { can } = useAuth();
    const canEdit = can("gl:admin");
    const [provider, setProvider] = useState("tally");
    const [items, setItems] = useState([]);
    const [version, setVersion] = useState(0);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ledger_name: "", note: "" });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/gl/mappings/effective?provider=${provider}`);
            setItems(data.items || []);
            setVersion(data.version || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load GL mappings"));
        } finally {
            setLoading(false);
        }
    }, [provider]);

    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return items;
        return items.filter((it) =>
            it.component_code.toLowerCase().includes(q) ||
            (it.ledger_name || "").toLowerCase().includes(q) ||
            (it.nature || "").toLowerCase().includes(q),
        );
    }, [items, search]);

    const overrideCount = useMemo(
        () => items.filter((it) => it.is_override).length,
        [items],
    );

    const openEdit = (row) => {
        setEditing(row);
        setForm({ ledger_name: row.ledger_name, note: "" });
        setOpen(true);
    };

    const submit = async () => {
        try {
            await api.post(`/gl/mappings/upsert`, {
                component_code: editing.component_code,
                provider,
                ledger_name: form.ledger_name.trim(),
                note: form.note,
            });
            toast.success(`Mapping updated · version bumped`);
            setOpen(false);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const reset = async (row) => {
        if (!window.confirm(`Reset mapping for ${row.component_code} on ${provider} back to system default?`)) return;
        try {
            await api.delete(`/gl/mappings/${row.component_code}/${provider}`);
            toast.success(`Mapping reset · version bumped`);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err, "Reset failed"));
        }
    };

    return (
        <div className="space-y-4" data-testid="gl-mappings-admin">
            <PageHeader
                title="GL Mappings"
                subtitle="System seed → Operator overrides → Export engine (D25). Mapping mutations bump the GL Mappings version, which is recorded on every export run (D29)."
                actions={
                    <Button variant="outline" size="sm" onClick={reload} data-testid="gl-mappings-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            <div className="flex flex-wrap items-center gap-2">
                <div className="flex border rounded overflow-hidden">
                    {["tally", "zoho"].map((p) => (
                        <button
                            key={p}
                            onClick={() => setProvider(p)}
                            data-testid={`gl-provider-${p}`}
                            className={`px-4 py-1.5 text-sm capitalize ${
                                provider === p
                                    ? "bg-slate-900 text-white"
                                    : "bg-white text-slate-700 hover:bg-slate-50"
                            }`}
                        >
                            {p === "tally" ? "Tally India" : "Zoho Books"}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-xs">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search component / ledger / nature…"
                        className="pl-8 h-8 text-sm"
                        data-testid="gl-mappings-search"
                    />
                </div>
                <div className="ml-auto flex items-center gap-2 text-xs">
                    <Badge className="bg-slate-100 text-slate-700">
                        Mapping version: <span className="font-mono ml-1">{version}</span>
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-800">
                        Overrides: {overrideCount}
                    </Badge>
                </div>
            </div>

            <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Component</th>
                            <th className="px-3 py-2 text-left">Nature</th>
                            <th className="px-3 py-2 text-left">Effective Ledger ({provider})</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-6 text-slate-400">Loading…</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-6 text-slate-400">No mappings</td></tr>
                        ) : (
                            filtered.map((row) => (
                                <tr key={row.component_code} className="border-b hover:bg-slate-50/50" data-testid={`gl-mapping-row-${row.component_code}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{row.component_code}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${NATURE_TONE[row.nature] || "bg-slate-100 text-slate-700"}`}>
                                            {row.nature}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2">{row.ledger_name}</td>
                                    <td className="px-3 py-2">
                                        {row.is_override ? (
                                            <Badge className="bg-amber-100 text-amber-800 text-[10px]">Override</Badge>
                                        ) : (
                                            <Badge className="bg-slate-100 text-slate-600 text-[10px]">System Seed</Badge>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-1">
                                        {canEdit && (
                                            <>
                                                <Button
                                                    variant="outline" size="sm"
                                                    onClick={() => openEdit(row)}
                                                    data-testid={`gl-mapping-edit-${row.component_code}`}
                                                >
                                                    <PencilSimple size={12} />
                                                </Button>
                                                {row.is_override && (
                                                    <Button
                                                        variant="outline" size="sm"
                                                        onClick={() => reset(row)}
                                                        data-testid={`gl-mapping-reset-${row.component_code}`}
                                                    >
                                                        <TrashSimple size={12} />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Override Mapping · {editing?.component_code}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Override the system-default ledger for this component. Saving bumps <code>gl_mappings_version</code>; historical exports remain reproducible against the snapshot they were generated with.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div>
                            <Label>Component</Label>
                            <Input value={editing?.component_code || ""} disabled className="font-mono" />
                        </div>
                        <div>
                            <Label>Provider</Label>
                            <Input value={provider} disabled className="capitalize" />
                        </div>
                        <div>
                            <Label>Ledger Name</Label>
                            <Input
                                value={form.ledger_name}
                                onChange={(e) => setForm({ ...form, ledger_name: e.target.value })}
                                placeholder="e.g. Salaries — Basic"
                                data-testid="gl-mapping-ledger-input"
                            />
                            <p className="text-[10px] text-slate-500 mt-1">
                                Must match the EXACT ledger name in your{" "}
                                {provider === "tally" ? "Tally" : "Zoho Books"} company.
                            </p>
                        </div>
                        <div>
                            <Label>Note</Label>
                            <Input
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                placeholder="Optional audit note"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={submit} data-testid="gl-mapping-save-btn">
                            <FloppyDisk size={14} className="mr-1" /> Save · Bump Version
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="text-[11px] text-slate-500 border-t pt-2">
                <span className="font-semibold">D25 contract</span>: System Seed → Operator Mapping → Export Engine.
                Overrides land in a versioned <code>gl_mappings</code> collection; each upsert/delete bumps the GL mappings counter (<code>gl_mappings_version</code>).
                Historical exports remain reproducible against the snapshot they were generated with (D29).
            </div>
        </div>
    );
}
